import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Reveal, Overline } from "./Reveal";
import { SERVICE_OPTIONS } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputBase =
  "w-full bg-transparent border-b border-white/20 pb-3 font-sans text-ink placeholder:text-faint focus:border-gold focus:outline-none transition-colors duration-300";

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-2">
    <label className="font-sans text-[0.65rem] tracking-[0.2em] uppercase text-ash">
      {label}
    </label>
    {children}
  </div>
);

const DateField = ({ label, date, setDate, testid }) => (
  <Field label={label}>
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`${inputBase} text-left ${date ? "text-ink" : "text-faint"}`}
        >
          {date ? format(date, "d LLLL yyyy") : "Select date"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-surface border-white/10" align="start">
        <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
      </PopoverContent>
    </Popover>
  </Field>
);

export default function Booking() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    guests: "",
    message: "",
  });
  const [arrival, setArrival] = useState(null);
  const [departure, setDeparture] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service) {
      toast.error("Please add your name, email and a service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        arrival: arrival ? format(arrival, "yyyy-MM-dd") : null,
        departure: departure ? format(departure, "yyyy-MM-dd") : null,
      });
      toast.success("Thank you — we'll be in touch to craft your stay.");
      setForm({ name: "", email: "", phone: "", service: "", guests: "", message: "" });
      setArrival(null);
      setDeparture(null);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="relative bg-bg py-28 md:py-40" data-testid="booking">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-4">
          <Reveal>
            <Overline>Contact</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              Book your <em className="italic text-metallic-gold">experience</em>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 font-sans text-base text-ink/60 max-w-sm leading-relaxed">
              For property owners, managers and travel professionals — and for
              guests staying at partner properties. Tell us about the property and
              the stay, and we&rsquo;ll shape the rest.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 space-y-6">
              <div>
                <span className="font-sans text-[0.65rem] tracking-[0.2em] uppercase text-ash">
                  Location
                </span>
                <p className="mt-1 font-serif text-2xl text-ink">Wānaka · Lakes District</p>
                <p className="mt-0.5 font-sans text-sm text-ash">Serving all of New Zealand</p>
              </div>
              <div>
                <span className="font-sans text-[0.65rem] tracking-[0.2em] uppercase text-ash">
                  Email
                </span>
                <a
                  href="mailto:hello@lodgeandretreatpartners.nz"
                  data-testid="contact-email"
                  className="mt-1 block font-serif text-2xl text-ink hover:text-gold transition-colors duration-300"
                >
                  hello@lodgeandretreatpartners.nz
                </a>
              </div>
              <div>
                <span className="font-sans text-[0.65rem] tracking-[0.2em] uppercase text-ash">
                  Phone
                </span>
                <a
                  href="tel:+642073035065"
                  data-testid="contact-phone"
                  className="mt-1 block font-serif text-2xl text-ink hover:text-gold transition-colors duration-300"
                >
                  +64 207 303 5065
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10"
            >
              <Field label="Full Name">
                <input
                  data-testid="input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  className={inputBase}
                />
              </Field>
              <Field label="Email">
                <input
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  className={inputBase}
                />
              </Field>
              <Field label="Phone (optional)">
                <input
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+64 …"
                  className={inputBase}
                />
              </Field>
              <Field label="Guests">
                <input
                  data-testid="input-guests"
                  value={form.guests}
                  onChange={update("guests")}
                  placeholder="e.g. 4 adults, 2 children"
                  className={inputBase}
                />
              </Field>

              <Field label="Service of Interest">
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm({ ...form, service: v })}
                >
                  <SelectTrigger
                    data-testid="select-service"
                    className={`${inputBase} rounded-none h-auto px-0`}
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-ink">
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} className="font-sans">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid grid-cols-2 gap-6">
                <DateField label="Arrival" date={arrival} setDate={setArrival} testid="date-arrival" />
                <DateField label="Departure" date={departure} setDate={setDeparture} testid="date-departure" />
              </div>

              <div className="md:col-span-2">
                <Field label="Tell us about your stay">
                  <textarea
                    data-testid="input-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={3}
                    placeholder="Dates, occasion, dietary needs, anything special…"
                    className={`${inputBase} resize-none`}
                  />
                </Field>
              </div>

              <div className="md:col-span-2 pt-2">
                <motion.button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-enquiry"
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-4 rounded-full bg-metallic-gold px-10 py-4 font-sans text-xs tracking-[0.2em] uppercase text-black hover:brightness-110 transition-all duration-300 disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send Enquiry"}
                  <span>→</span>
                </motion.button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
